/* ============================================================
   YOLÈTE — Magazine : base d'articles (Journal + Presse)
   ------------------------------------------------------------
   type: 'journal' = article de blog Yolète
   type: 'presse'  = relais d'un article paru dans un magazine
                     physique (champs source / issue / link).
   `body` = HTML de l'article.
   Depuis le 8 oct 2026, ces 14 articles sont aussi dans le blog
   Shopify « Yolète » et se modifient dans l'admin (Boutique en
   ligne > Articles de blog). Sur le thème, le blog fait foi : ce
   fichier ne sert plus que de repli (copie statique, blog vide).
   ============================================================ */

const ARTICLES = [
  {
    slug: 'ceinture-boucle-interchangeable',
    type: 'journal',
    title: 'La ceinture à boucle interchangeable, comment ça marche',
    excerpt: 'Une ceinture en cuir, plusieurs boucles bijoux. Le principe Yolète expliqué en trois minutes, du clip à la taille.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__philippe-sea.jpg',
    date: '2026-09-12',
    author: 'L\'atelier Yolète',
    readMins: 3,
    featured: true,
    body: `
      <p class="lead">Une ceinture à boucle interchangeable, c'est une ceinture en cuir dont la boucle se retire et se remplace en un geste. Chez Yolète, la ceinture s'appelle la tige et la boucle devient un bijou.</p>
      <h2>Une ceinture, plusieurs boucles</h2>
      <p>La tige est une ceinture en cuir façonnée à la main dans le Cantal, sans boucle fixe. Son extrémité porte un système de clip sur lequel vient se poser n'importe quelle boucle Yolète de la même largeur. Une ceinture noire en 40 mm accueille ainsi une boucle dorée le matin, une boucle argentée le soir, un concho le week-end.</p>
      <h2>Comment on change la boucle</h2>
      <p>Posez la boucle sur le clip de la ceinture et appuyez dans l'axe jusqu'au clic. Pour la retirer, tirez droit, jamais de côté. Aucun outil, aucune vis, dix secondes montre en main. Le clip est en métal et tient des années.</p>
      <h2>Deux largeurs</h2>
      <p>La ceinture 40 mm est la largeur signature, celle qui porte les grandes boucles bijoux sur un jean ou une robe. La ceinture 25 mm, plus fine, se glisse dans les passants d'un pantalon de tailleur et reçoit les boucles 25 mm. Une boucle 40 mm ne se monte pas sur une ceinture 25 mm, et inversement.</p>
      <h2>Choisir sa taille de ceinture</h2>
      <p>Mesurez une ceinture que vous portez déjà, de la boucle au trou utilisé. Cette longueur en centimètres est votre taille Yolète. Entre deux tailles, prenez la plus grande, le cuir se détend légèrement à l'usage. Les tailles vont de 75 à 110.</p>
      <h2>Pourquoi c'est une bonne idée</h2>
      <p>Une seule ceinture en cuir de qualité, qui se patine avec vous, et des boucles qui changent au gré des envies. On achète moins de ceintures, on les garde plus longtemps, et l'on offre une boucle plutôt qu'une ceinture entière.</p>
      <blockquote>« La ceinture reste, la boucle change. C'est toute l'idée. »</blockquote>
      <p><a href="/pages/personnalisation">Composer sa ceinture</a> ou <a href="/pages/produits?type=tige">voir les ceintures en cuir</a>.</p>
    `,
  },
  {
    slug: 'choisir-sa-boucle-selon-son-jean',
    type: 'journal',
    title: 'Comment choisir sa boucle selon son jean ?',
    excerpt: 'Brut, noir ou taille haute, chaque jean appelle sa boucle. Voici comment accorder les deux.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__philippe-stairs.jpg',
    date: '2026-07-05',
    author: 'L\'atelier Yolète',
    readMins: 3,
    featured: false,
    body: `
      <p class="lead">Le jean est la toile de fond idéale pour une boucle bijou. Reste à accorder la finition et la taille de la boucle à la coupe.</p>
      <h2>Sur un jean brut</h2>
      <p>Une boucle dorée ou vieil or réchauffe le bleu profond du denim. Posez-la sur une tige cuir marron 40 mm et laissez la lumière jouer sur le métal.</p>
      <h2>Sur un jean noir</h2>
      <p>Le vieil argent tranche avec élégance sur un total look sombre. Une seule boucle statement suffit à structurer la silhouette.</p>
      <h2>Sur un jean taille haute</h2>
      <p>La taille haute place la boucle pile au centre du regard. Choisissez une pièce forte, concho ou sertie, et gardez le reste sobre.</p>
      <blockquote>« La bonne boucle ne se choisit pas au hasard. Elle prolonge votre allure. »</blockquote>
    `,
  },
  {
    slug: 'dore-ou-argente-quelle-boucle',
    type: 'journal',
    title: 'Doré ou argenté, quelle boucle pour quel style ?',
    excerpt: 'Deux finitions, deux personnalités. Le doré réchauffe, l\'argenté apaise. On vous aide à trancher.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__buckle-fish.jpg',
    date: '2026-07-04',
    author: 'L\'atelier Yolète',
    readMins: 3,
    featured: false,
    body: `
      <p class="lead">Une même boucle existe souvent en doré et en argenté. Le choix n'est pas anodin, il oriente toute l'allure.</p>
      <h2>Le doré, pour réchauffer</h2>
      <p>Le doré et le vieil or subliment les carnations chaudes et les tenues terreuses. Ils apportent une touche solaire, un peu couture, parfaite l'été.</p>
      <h2>L'argenté, pour apaiser</h2>
      <p>L'argent et le vieil argent posent une note plus graphique et contemporaine. Ils s'accordent au noir, au denim et aux teintes froides.</p>
      <h2>Et si on mélangeait ?</h2>
      <p>Rien n'interdit de collectionner les deux. Une tige, deux boucles, et vous passez du chaud au froid selon l'envie du jour.</p>
      <blockquote>« Le métal d'une boucle raconte déjà une humeur. »</blockquote>
    `,
  },
  {
    slug: 'pourquoi-la-boucle-devient-bijou',
    type: 'journal',
    title: 'Pourquoi la boucle devient le bijou de la ceinture ?',
    excerpt: 'La boucle n\'est plus une simple attache. Chez Yolète, elle se porte et se collectionne comme un bijou.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__belt-detail.jpg',
    date: '2026-07-03',
    author: 'L\'atelier Yolète',
    readMins: 4,
    featured: false,
    body: `
      <p class="lead">Pendant longtemps, la boucle a été un détail fonctionnel. Yolète en a fait une pièce de désir à part entière.</p>
      <p>Chaque boucle est dessinée puis ciselée comme une pièce de joaillerie, en laiton vieilli, vieil argent ou or ancien. Certaines sont même émaillées à la main.</p>
      <h2>Un bijou que l'on change</h2>
      <p>Parce qu'elle se clipse et se retire en un geste, la boucle devient interchangeable. Vous en possédez plusieurs et vous les accordez à vos humeurs, comme une bague ou une broche.</p>
      <h2>Un objet à collectionner</h2>
      <p>Deux collections paraissent chaque année, avec près de 45 nouvelles boucles à chaque saison. De quoi commencer une collection, puis l'agrandir.</p>
      <blockquote>« Une boucle n'est plus une boucle. C'est un bijou que l'on porte autour de la taille. »</blockquote>
    `,
  },
  {
    slug: 'une-tige-cinq-boucles-cinq-looks',
    type: 'journal',
    title: 'Une tige, cinq boucles, cinq looks',
    excerpt: 'La démonstration par l\'exemple. Une seule tige en cuir, cinq boucles, et cinq allures très différentes.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__mina-stairs.jpg',
    date: '2026-07-02',
    author: 'L\'atelier Yolète',
    readMins: 3,
    featured: false,
    body: `
      <p class="lead">C'est tout l'esprit Yolète. Une tige neutre, et la boucle qui fait basculer le look.</p>
      <h2>1. La boucle intemporelle</h2>
      <p>Pour tous les jours, une valeur sûre qui s'accorde à tout.</p>
      <h2>2. La boucle western</h2>
      <p>Concho et esprit navajo, pour une allure Santa Fe assumée.</p>
      <h2>3. La boucle sertie</h2>
      <p>Une pièce bijou qui transforme un jean simple en tenue habillée.</p>
      <h2>4. La boucle émaillée</h2>
      <p>De la couleur émaillée à la main, pour réveiller une tenue sobre.</p>
      <h2>5. La boucle statement</h2>
      <p>Grande, sculptée, impossible à rater. Elle devient le centre du regard.</p>
      <blockquote>« Une tige, mille boucles, et l'allure change à l'infini. »</blockquote>
    `,
  },
  {
    slug: 'boucle-interchangeable-notre-histoire',
    type: 'journal',
    title: 'La boucle interchangeable, la petite idée devenue signature',
    excerpt: "Comment une obsession née dans un village du Cantal a transformé l'accessoire le plus banal en objet de désir.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__couple.jpg',
    date: '2026-05-18',
    author: 'L\'atelier Yolète',
    readMins: 4,
    featured: true,
    body: `
      <p class="lead">Et si la boucle n'était plus prisonnière de sa ceinture ? C'est la question, presque enfantine, qui a tout déclenché à Yolet, petit village du Cantal qui a donné son nom à la maison.</p>
      <p>Depuis une quinzaine d'années, nous coupons nos tiges en cuir dans nos ateliers du Cantal et dessinons des boucles comme on dessine des bijoux. L'idée tient en peu de mots. Une tige, des dizaines de boucles, et la liberté de changer d'allure en un clic.</p>
      <h2>Une tige, mille boucles</h2>
      <p>Vous choisissez votre boucle bijou, puis vous l'associez à une tige en cuir, lisse ou imprimé, en 25 ou 40 mm. Une nouvelle envie, une nouvelle tenue, et il suffit de changer la boucle. C'est tout le concept Yolète.</p>
      <blockquote>« Une boucle n'est plus une boucle. C'est un bijou que l'on porte autour de la taille. »</blockquote>
      <h2>Le geste juste, façonné à la main</h2>
      <p>Cuirs au tannage végétal, plus responsables, et deux collections par an, une pour l'été et une pour l'hiver. Chaque pièce est pensée pour durer, et pour se réinventer.</p>
    `,
  },
  {
    slug: 'entretenir-cuir-tannage-vegetal',
    type: 'journal',
    title: 'Entretenir vos cuirs au tannage végétal',
    excerpt: 'Nos gestes simples pour garder une tige Yolète belle pendant des années.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__bag-yolete.jpg',
    date: '2026-04-29',
    author: 'L\'atelier Yolète',
    readMins: 3,
    featured: false,
    body: `
      <p class="lead">Un cuir au tannage végétal vit, patine et se bonifie avec le temps, à condition de quelques attentions.</p>
      <h2>Au quotidien</h2>
      <p>Évitez l'eau et les sources de chaleur directes. Un chiffon doux et sec suffit à raviver l'éclat après une journée portée.</p>
      <h2>Une à deux fois par an</h2>
      <p>Nourrissez le cuir avec un lait ou une crème incolore adaptée, en couche fine. Laissez pénétrer, puis lustrez délicatement.</p>
      <h2>Le rangement</h2>
      <p>Conservez votre tige à plat ou enroulée sans pli marqué, à l'abri de la lumière directe.</p>
    `,
  },
  {
    slug: 'yolete-fault-magazine',
    type: 'presse',
    title: 'Yolète en édito mode dans Fault Magazine',
    excerpt: "Le magazine de mode britannique intègre les ceintures et boucles Yolète dans une série studio graphique et sombre.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/press__fault-1.jpg',
    date: '2026-07-01',
    source: 'Fault Magazine',
    issue: 'Juillet 2026',
    link: 'https://www.instagram.com/yoletefrance/',
    readMins: 2,
    featured: false,
    body: `
      <p class="lead">Fault Magazine signe une série mode où la ceinture n'est plus un simple détail. Elle structure la silhouette.</p>
      <p>Sur des tailleurs de cuir et des mailles graphiques, les tiges Yolète et leurs boucles ciselées apportent la touche joaillière de la série. Une mise en lumière qui confirme la place de la maison dans le vestiaire mode.</p>
      <blockquote>« La boucle comme un bijou, portée au centre de la silhouette. »</blockquote>
    `,
  },
  {
    slug: 'yolete-version-femina-echappee-sauvage',
    type: 'presse',
    title: 'Échappée sauvage, Yolète dans Version Femina',
    excerpt: "Les imprimés fauves à l'honneur, la ceinture Yolète habille une échappée mode signée Version Femina.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/press__version-femina.jpg',
    date: '2026-07-01',
    source: 'Version Femina',
    issue: 'Juillet 2026',
    link: 'https://www.instagram.com/yoletefrance/',
    readMins: 2,
    featured: false,
    body: `
      <p class="lead">« Les imprimés fauves pimentent nos dressings. On les assume en toute liberté ! » C'est le ton de l'échappée mode « Échappée sauvage » de Version Femina.</p>
      <p>Dans les décors minéraux d'un désert, la silhouette léopard est ceinturée d'une pièce Yolète, un clin d'œil parfait à l'esprit modulable et solaire de la maison.</p>
      <p>Photos June Miller · Réalisation Melissa Pigois.</p>
    `,
  },
  {
    slug: 'yolete-modzik-montemarco',
    type: 'presse',
    title: 'Montemarco en couverture de MODZIK, ceinturé Yolète',
    excerpt: "L'artiste Montemarco fait la couverture du magazine musical MODZIK, une boucle concho Yolète à la taille.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/press__modzik-montemarco.jpg',
    date: '2026-06-20',
    source: 'MODZIK',
    issue: 'N°45',
    link: 'https://www.instagram.com/yoletefrance/',
    readMins: 2,
    featured: false,
    body: `
      <p class="lead">« Prendre la parole », c'est le thème de la couverture n°45 de MODZIK, qui met en scène l'artiste Montemarco.</p>
      <p>Sur un total look noir, la ceinture à imprimé vache et sa boucle concho Yolète signent le look. Une belle démonstration que le bijou de taille se porte aussi au masculin, sur la scène musicale.</p>
    `,
  },
  {
    slug: 'yolete-rolling-stone-africa-piway',
    type: 'presse',
    title: 'Yolète repérée par Rolling Stone Africa',
    excerpt: "Dans un shooting street signé Piway, la boucle Yolète se glisse dans un vestiaire cuir affirmé.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/press__piway-rollingstone.jpg',
    date: '2026-06-10',
    source: 'Rolling Stone Africa',
    link: 'https://www.instagram.com/yoletefrance/',
    readMins: 2,
    featured: false,
    body: `
      <p class="lead">Veste en cuir tressé, lunettes fumées et lumière dorée, le shooting street relayé par Rolling Stone Africa met la boucle Yolète en gros plan.</p>
      <p>Tenue en main puis portée à la taille, la boucle devient l'accessoire-signature d'une allure contemporaine et internationale.</p>
    `,
  },
  {
    slug: 'yolete-la-montagne-boutique-montpellier',
    type: 'presse',
    title: "Depuis Aurillac, Yolète monte d'un cran et ouvre sa boutique",
    excerpt: "La Montagne raconte le beau parcours de Luc Miraglia, de la véranda de Yolet à une boutique en plein Montpellier.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/p-lux976.jpg',
    date: '2024-12-05',
    source: 'La Montagne',
    issue: '5 décembre 2024',
    link: 'https://www.lamontagne.fr',
    readMins: 3,
    featured: false,
    body: `
      <p class="lead">« Ça sent le cuir à plein nez à Yolète, la marque de maroquinerie née à Yolet. »</p>
      <p>Dans son édition du 5 décembre 2024, La Montagne retrace le parcours de Luc Miraglia, un autodidacte qui a lancé sa marque en « bidouillant » le cuir dans une véranda du village cantalien de Yolet, avant de fêter bientôt les quinze ans de la maison.</p>
      <p>Le journal annonce l'ouverture, en plein centre historique de Montpellier, de la première boutique de la marque, un « retour aux sources » pour le fondateur, natif de la ville. À Aurillac, sous le viaduc, la maison s'est aussi dotée de 120 m² supplémentaires dédiés aux machines de maroquinerie.</p>
      <blockquote>« Derrière ses boucles de ceinture à clipser à l'infini se cache un joli succès made in Aurillac. »</blockquote>
    `,
  },
  {
    slug: 'yolete-views-google-france',
    type: 'presse',
    title: 'Yolète dans le viseur de Views × Google France',
    excerpt: "Street style parisien, la ceinture Yolète s'invite dans une série mode signée Views et Google France.",
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/press__views-google.jpg',
    date: '2026-06-12',
    source: 'Views × Google',
    issue: 'Juin 2026',
    link: 'https://www.instagram.com/yoletefrance/',
    readMins: 1,
    featured: false,
    body: `
      <p class="lead">Devanture de boutique, pavés parisiens et silhouette streetwear, Views et Google France captent l'énergie de la mode urbaine.</p>
      <p>Au centre du cadre, une ceinture Yolète vient ponctuer le look et prouve que le bijou de taille trouve aussi sa place dans le vestiaire street.</p>
    `,
  },
  {
    slug: 'cinq-facons-de-porter-sa-ceinture',
    type: 'journal',
    title: '5 façons de porter sa ceinture cet été',
    excerpt: 'De la robe fluide au jean taille haute, nos associations préférées tige et boucle.',
    cover: 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__equestre-bag.jpg',
    date: '2026-06-10',
    author: 'L\'atelier Yolète',
    readMins: 5,
    featured: false,
    body: `
      <p class="lead">Une seule ceinture, cinq silhouettes. La preuve par l'exemple avec nos tiges et nos boucles bijou.</p>
      <h2>1. Sur une robe fluide</h2>
      <p>Une tige fine 25 mm et une boucle concho pour marquer la taille sans alourdir.</p>
      <h2>2. Avec un jean taille haute</h2>
      <p>La tige 40 mm imprimée léopard et une boucle baroque, pour une allure western chic.</p>
      <h2>3. Sur un blazer oversize</h2>
      <p>Fermez la veste d'une ceinture à boucle bijou pour une touche couture.</p>
      <h2>4. En total look uni</h2>
      <p>Laissez la boucle parler avec un cuir noir et une boucle vieil argent.</p>
      <h2>5. Pour lui</h2>
      <p>Une tige sobre et une boucle aigle, parce que la signature se porte aussi au masculin.</p>
    `,
  },
];

/* ---------- Articles du blog Shopify (7 oct 2026) ----------
   Sur le thème, le snippet yolete-blog publie window.YOLETE_BLOG : les
   articles que Charlotte écrit dans l'admin Shopify (blog « Yolète »), le plus
   récent d'abord. Dès que le blog a un article, il remplace toute la liste de
   ce fichier : un article masqué ou supprimé dans l'admin disparaît du
   magazine (8 oct 2026). Sur la copie statique, la liste est vide. */
(function () {
  var B = (typeof window !== 'undefined' && Array.isArray(window.YOLETE_BLOG)) ? window.YOLETE_BLOG : [];
  if (!B.length) return;
  var COUV = 'https://cdn.shopify.com/s/files/1/0790/8714/1213/files/campaign__philippe-sea.jpg';      /* couverture de repli, article sans image */
  var neufs = B.filter(function (a) { return a && a.slug && a.title; }).map(function (a) {
    return {
      slug: a.slug, url: a.url || '', shopify: true, type: a.type === 'presse' ? 'presse' : 'journal',
      title: a.title, excerpt: a.excerpt || '', cover: a.cover || COUV,
      date: a.date || new Date().toISOString().slice(0, 10), author: a.author || "L'atelier Yolète",
      readMins: a.readMins || 2, featured: !!a.featured,
      source: a.source || '', issue: a.issue || '', link: a.link || '', body: a.body || '',
    };
  });
  if (!neufs.length) return;
  ARTICLES.length = 0;
  ARTICLES.push.apply(ARTICLES, neufs);
})();

/* Adresse d'un article : celle de Shopify (/blogs/yolete/<handle>) pour un
   article écrit dans l'admin, la page article du site pour les autres. */
function articleHref(a) { return a.url ? a.url : '/pages/article?slug=' + a.slug; }

/* Corps d'un article écrit dans l'éditeur Shopify : on garde la mise en forme
   (titres, gras, listes, liens, images) mais on retire les styles collés
   depuis un autre logiciel, qui casseraient la typographie du magazine.
   Seule la classe « lead » (chapô des articles repris du site) est conservée. */
function corpsPropre(html) {
  var t = document.createElement('template');
  t.innerHTML = String(html || '');
  t.content.querySelectorAll('meta, script, style, iframe:not([src*="youtube"]):not([src*="vimeo"])').forEach(function (n) { n.remove(); });
  t.content.querySelectorAll('*').forEach(function (n) {
    n.removeAttribute('style'); n.removeAttribute('id');
    if (n.classList.contains('lead')) n.className = 'lead'; else n.removeAttribute('class');
    if (n.tagName === 'SPAN' && !n.attributes.length) n.replaceWith.apply(n, Array.prototype.slice.call(n.childNodes));
  });
  t.content.querySelectorAll('a[href^="http"]').forEach(function (a) { if (a.hostname !== location.hostname) { a.target = '_blank'; a.rel = 'noopener'; } });
  t.content.querySelectorAll('p').forEach(function (p) { if (!p.textContent.replace(/\u00a0/g, ' ').trim() && !p.querySelector('img')) p.remove(); });
  return t.innerHTML;
}

/* ---------- Helpers ---------- */
const ARTICLE_TYPE = { journal: 'Le Journal', presse: 'Presse' };
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

function frDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS_FR[m - 1]} ${y}`;
}

/* Ligne meta : journal -> date · durée ; presse -> source · numéro */
function articleMeta(a) {
  return a.type === 'presse'
    ? [a.source, a.issue].filter(Boolean).join(' · ')
    : `${frDate(a.date)} · ${a.readMins} min`;
}
